/**
 * SourcePicker — пошук моба/NPC у джерелах (adventures + wiki bestiary/npcs).
 *
 * Викликає `/api/sources/combatants?q=...` (GM-only). При кліку «Додати»
 * викидає payload `Partial<Combatant>` назовні; EncounterGm робить
 * `combatant:upsert` через WS.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Combatant, CreatureBucket, SourceCombatant, SourceListResponse } from '@sd/shared';
import { CREATURE_BUCKET_LABELS } from '@sd/shared';
import { fetchJson } from '../../lib/gm.js';

interface Props {
  onAdd: (payload: Partial<Combatant>) => void;
  onClose: () => void;
}

const DEFAULT_TOKEN_COLORS: Record<string, string> = {
  adversary: '#9d4f4a',
  ally: '#6f8d54',
  player: '#cca766',
};

function suggestedCombatant(s: SourceCombatant): Partial<Combatant> {
  const stats = s.stats ?? {};
  // Нижня межа 1 — щоб мінйони (1 HP) лишались мінйонами. Верхня 30 — для Solo/босів.
  const hpMax = Math.max(1, Math.min(30, stats.hpMax ?? 6));
  const stressMax = Math.max(0, Math.min(12, stats.stressMax ?? 6));
  const armorMax = Math.max(0, Math.min(8, stats.armorMax ?? 0));
  return {
    kind: s.suggestedKind,
    name: s.name,
    sourceId: s.sourceKey,
    icon: s.icon,
    illustration: s.image,
    stats: {
      difficulty: stats.difficulty,
      hpMax,
      hp: 0, // Daggerheart cumulative marks: 0 = повністю здоровий
      stressMax,
      stress: 0,
      armorMax,
      armor: 0,
      thresholdMajor: stats.thresholdMajor,
      thresholdSevere: stats.thresholdSevere,
      attackMod: stats.attackMod,
      attacks: stats.attacks ?? [],
      features: stats.features ?? [],
      tactics: s.tactics ?? stats.tactics,
      motivation: s.motivation ?? stats.motivation,
      tier: s.tier,
      dhType: s.role,
    },
    conditions: [],
    tokenX: 5,
    tokenY: 5,
    tokenColor: DEFAULT_TOKEN_COLORS[s.suggestedKind] ?? '#cca766',
    hidden: false,
    isMinionGroup: false,
    count: 1,
  };
}

const DH_TYPES = ['Solo', 'Bruiser', 'Horde', 'Minion', 'Leader', 'Ranged', 'Skulk', 'Social', 'Standard', 'Support'];
const BUCKETS: CreatureBucket[] = ['undead', 'human', 'elf', 'beast', 'monster', 'abomination', 'construct', 'elemental', 'spirit', 'other'];

type NumKey = 'hpMin' | 'hpMax' | 'stressMin' | 'stressMax' | 'armorMin' | 'armorMax' | 'diffMin' | 'diffMax';
type NumState = Partial<Record<NumKey, string>>;

function toggle<T>(set: Set<T>, v: T): Set<T> {
  const next = new Set(set);
  if (next.has(v)) next.delete(v); else next.add(v);
  return next;
}
function inRange(v: number | undefined, min: string | undefined, max: string | undefined): boolean {
  if (min !== undefined && min !== '') { if (v === undefined || v < Number(min)) return false; }
  if (max !== undefined && max !== '') { if (v === undefined || v > Number(max)) return false; }
  return true;
}

export function SourcePicker({ onAdd, onClose }: Props): JSX.Element {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState<SourceCombatant[]>([]);

  const [origins, setOrigins] = useState<Set<'symbaroum' | 'daggerheart'>>(new Set());
  const [tiers, setTiers] = useState<Set<1 | 2 | 3 | 4>>(new Set());
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [buckets, setBuckets] = useState<Set<CreatureBucket>>(new Set());
  const [num, setNum] = useState<NumState>({});
  const [showAdv, setShowAdv] = useState(false);

  // Завантажуємо ВЕСЬ список одноразово; усе фільтрування — на клієнті.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchJson<SourceListResponse>('/api/sources/combatants?q=&limit=500')
      .then((res) => { if (alive) setAll(res.items); })
      .catch((err: Error) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const setN = (k: NumKey, v: string): void => setNum((p) => ({ ...p, [k]: v }));
  const resetFilters = (): void => {
    setQuery(''); setOrigins(new Set()); setTiers(new Set());
    setTypes(new Set()); setBuckets(new Set()); setNum({});
  };
  const anyFilter = !!query || origins.size || tiers.size || types.size || buckets.size
    || Object.values(num).some((v) => v !== undefined && v !== '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((it) => {
      if (q && !it.name.toLowerCase().includes(q) && !(it.raceTag ?? '').toLowerCase().includes(q)) return false;
      const origin = it.source === 'dh-srd' ? 'daggerheart' : 'symbaroum';
      if (origins.size && !origins.has(origin)) return false;
      if (tiers.size && !tiers.has(it.tier)) return false;
      if (types.size && !(it.role && types.has(it.role))) return false;
      if (buckets.size && !buckets.has(it.bucket)) return false;
      const st = it.stats ?? {};
      if (!inRange(st.hpMax, num.hpMin, num.hpMax)) return false;
      if (!inRange(st.stressMax, num.stressMin, num.stressMax)) return false;
      if (!inRange(st.armorMax, num.armorMin, num.armorMax)) return false;
      if (!inRange(st.difficulty, num.diffMin, num.diffMax)) return false;
      return true;
    }).sort((a, b) => (a.tier - b.tier) || a.name.localeCompare(b.name, 'uk'));
  }, [all, query, origins, tiers, types, buckets, num]);

  const grouped = useMemo(() => ({
    dh: filtered.filter((i) => i.source === 'dh-srd'),
    adv: filtered.filter((i) => i.source === 'adventure'),
    wiki: filtered.filter((i) => i.source === 'wiki'),
  }), [filtered]);

  return (
    <div className="src-picker">
      <div className="src-head">
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Шукати: ім'я, раса, ключове слово…"
          onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        />
        <div className="src-stats">
          {loading ? 'завантажую…' : error ? `помилка: ${error}` : `${filtered.length} з ${all.length}`}
        </div>
        {anyFilter && (
          <button type="button" className="src-reset" onClick={resetFilters} title="Скинути всі фільтри">Скинути</button>
        )}
      </div>

      {/* Походження */}
      <div className="src-filter-row">
        <span className="src-flabel">Походження:</span>
        <button type="button" className={`src-fbtn orig-dh ${origins.has('daggerheart') ? 'on' : ''}`}
          onClick={() => setOrigins((s) => toggle(s, 'daggerheart'))}>Daggerheart</button>
        <button type="button" className={`src-fbtn orig-sym ${origins.has('symbaroum') ? 'on' : ''}`}
          onClick={() => setOrigins((s) => toggle(s, 'symbaroum'))}>Symbaroum</button>
      </div>

      {/* Тір */}
      <div className="src-filter-row">
        <span className="src-flabel">Тір:</span>
        {([1, 2, 3, 4] as const).map((t) => (
          <button key={t} type="button" className={`src-fbtn t${t} ${tiers.has(t) ? 'on' : ''}`}
            onClick={() => setTiers((s) => toggle(s, t))}>T{t}</button>
        ))}
      </div>

      {/* Тип (DH-роль) */}
      <div className="src-filter-row">
        <span className="src-flabel">Тип:</span>
        {DH_TYPES.map((t) => (
          <button key={t} type="button" className={`src-fbtn ${types.has(t) ? 'on' : ''}`}
            onClick={() => setTypes((s) => toggle(s, t))}>{t}</button>
        ))}
      </div>

      {/* Раса / категорія істоти */}
      <div className="src-filter-row">
        <span className="src-flabel">Раса:</span>
        {BUCKETS.map((b) => (
          <button key={b} type="button" className={`src-fbtn ${buckets.has(b) ? 'on' : ''}`}
            onClick={() => setBuckets((s) => toggle(s, b))}>{CREATURE_BUCKET_LABELS[b]}</button>
        ))}
      </div>

      {/* Числові діапазони */}
      <div className="src-filter-row">
        <button type="button" className="src-adv-toggle" onClick={() => setShowAdv((v) => !v)}>
          {showAdv ? '▾' : '▸'} Числові фільтри
        </button>
        {showAdv && (
          <div className="src-nums">
            <NumRange label="HP" minK="hpMin" maxK="hpMax" num={num} setN={setN} />
            <NumRange label="Стрес" minK="stressMin" maxK="stressMax" num={num} setN={setN} />
            <NumRange label="Броня" minK="armorMin" maxK="armorMax" num={num} setN={setN} />
            <NumRange label="Difficulty" minK="diffMin" maxK="diffMax" num={num} setN={setN} />
          </div>
        )}
      </div>

      <div className="src-body">
        {grouped.dh.length > 0 && (
          <SourceGroup title="Daggerheart SRD (офіційні)" items={grouped.dh} onAdd={(s) => onAdd(suggestedCombatant(s))} />
        )}
        {grouped.adv.length > 0 && (
          <SourceGroup title="З пригод" items={grouped.adv} onAdd={(s) => onAdd(suggestedCombatant(s))} />
        )}
        {grouped.wiki.length > 0 && (
          <SourceGroup title="З wiki (бестіарій + NPC)" items={grouped.wiki} onAdd={(s) => onAdd(suggestedCombatant(s))} />
        )}
        {!loading && filtered.length === 0 && (
          <p className="src-empty">Нічого не знайдено. Спробуй зняти частину фільтрів.</p>
        )}
      </div>
    </div>
  );
}

function NumRange({ label, minK, maxK, num, setN }: {
  label: string; minK: NumKey; maxK: NumKey; num: NumState; setN: (k: NumKey, v: string) => void;
}): JSX.Element {
  return (
    <div className="src-num">
      <span className="src-num-label">{label}</span>
      <input type="number" placeholder="від" value={num[minK] ?? ''} onChange={(e) => setN(minK, e.target.value)} />
      <span className="src-num-dash">–</span>
      <input type="number" placeholder="до" value={num[maxK] ?? ''} onChange={(e) => setN(maxK, e.target.value)} />
    </div>
  );
}

function SourceGroup({ title, items, onAdd }: {
  title: string;
  items: SourceCombatant[];
  onAdd: (s: SourceCombatant) => void;
}): JSX.Element {
  return (
    <div className="src-group">
      <h4 className="src-group-title">{title} <span className="src-group-count">{items.length}</span></h4>
      <div className="src-grid">
        {items.map((s) => <SourceCard key={s.sourceKey} item={s} onAdd={() => onAdd(s)} />)}
      </div>
    </div>
  );
}

function SourceCard({ item, onAdd }: { item: SourceCombatant; onAdd: () => void }): JSX.Element {
  const s = item.stats ?? {};
  return (
    <div className={`src-card kind-${item.suggestedKind}`}>
      <div className="src-card-head">
        <div className="src-card-titles">
          <h5>{item.name}</h5>
          <div className="src-card-sub">{item.sourceLabel}</div>
        </div>
        <div className="src-card-tags">
          <span className={`src-card-origin ${item.source === 'dh-srd' ? 'dh' : 'sym'}`}>
            {item.source === 'dh-srd' ? 'Daggerheart' : 'Symbaroum'}
          </span>
          <span className={`src-card-tier t${item.tier}`}>T{item.tier}</span>
          {item.role && <span className="src-card-role">{item.role}</span>}
          {item.raceTag && <span className="src-card-race">{item.raceTag}</span>}
        </div>
      </div>

      <div className="src-card-stats">
        {s.difficulty !== undefined && <Stat label="Diff" v={s.difficulty} />}
        {s.hpMax !== undefined && <Stat label="HP" v={s.hpMax} />}
        {s.stressMax !== undefined && <Stat label="Str" v={s.stressMax} />}
        {s.thresholdMajor !== undefined && <Stat label="ThrM" v={s.thresholdMajor} />}
        {s.attackMod !== undefined && <Stat label="+Atk" v={s.attackMod} />}
        {(s.attacks?.length ?? 0) > 0 && <Stat label="Wpn" v={s.attacks!.length} />}
        {(s.features?.length ?? 0) > 0 && <Stat label="Feat" v={s.features!.length} />}
      </div>

      {item.summary && <p className="src-card-summary">{item.summary}</p>}
      {item.tactics && !item.summary && <p className="src-card-summary">{item.tactics.slice(0, 160)}…</p>}

      <button type="button" className="src-card-add" onClick={onAdd}>У зустріч</button>
    </div>
  );
}

function Stat({ label, v }: { label: string; v: number }): JSX.Element {
  return (
    <span className="src-stat">
      <span className="src-stat-lbl">{label}</span>
      <span className="src-stat-val">{v}</span>
    </span>
  );
}
